import { Sparkles, Lock, Wand2, Loader2, ArrowLeft, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MemoInput } from "@/components/MemoInput";

const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hl = (row, f) => row.suggested?.includes(f) ? "ring-1 ring-amber-300 bg-amber-50/70" : "";

const ReviewRow = ({ r, i, stores, categoriesFor, isPositiveType, onEdit }) => {
  const set = (k, v) => onEdit(i, { ...r, [k]: v, suggested: (r.suggested || []).filter(x => x !== k) });
  return (
    <tr className={`border-t border-stone-100 align-top ${r.locked ? "opacity-50" : ""}`} data-testid={`review-row-${i}`}>
      <td className="px-2 py-1.5 whitespace-nowrap text-stone-600">{r.locked && <Lock className="inline w-3 h-3 mr-1 text-emerald-700" />}{r.date}</td>
      <td className="px-2 py-1.5 text-stone-800 max-w-[140px] truncate" title={r.vendor}>{r.vendor || <span className="text-stone-400">—</span>}</td>
      <td className={`px-2 py-1.5 text-right whitespace-nowrap font-medium ${isPositiveType(r.type) ? "text-emerald-700" : ""}`}>{isPositiveType(r.type) ? "+" : "−"}{fmt(r.amount)}</td>
      <td className="px-2 py-1 min-w-[200px]">
        <MemoInput value={r.description} vendor={r.vendor} onChange={v => set("description", v)} className={`h-7 text-xs ${hl(r, "description")}`} testId={`review-memo-${i}`} disabled={r.locked} />
      </td>
      <td className="px-2 py-1">
        <Select value={r.category} onValueChange={v => set("category", v)} disabled={r.locked}>
          <SelectTrigger className={`h-7 w-40 text-xs ${hl(r, "category")}`} data-testid={`review-category-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{categoriesFor(r.type).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        {r.category_from_rule && <span className="text-[9px] text-sky-700 flex items-center gap-0.5 mt-0.5"><Wand2 className="w-2.5 h-2.5" /> rule</span>}
      </td>
      <td className="px-2 py-1">
        <Select value={r.store_id || "general"} onValueChange={v => set("store_id", v === "general" ? null : v)} disabled={r.locked}>
          <SelectTrigger className={`h-7 w-36 text-xs ${hl(r, "store_id")}`} data-testid={`review-store-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem>
            {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
    </tr>
  );
};

export const ImportReview = ({ rows, setRows, stores, categoriesFor, isPositiveType, onBack, onConfirm, busy, confirmLabel }) => {
  const suggestedCount = rows.filter(r => r.suggested?.length).length;
  const importable = rows.filter(r => !r.locked).length;
  const editRow = (i, row) => setRows(rows.map((x, j) => j === i ? row : x));
  return (
    <div className="space-y-4" data-testid="import-review">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-stone-700">
          <span className="font-semibold">{rows.length}</span> rows ready.{" "}
          {suggestedCount > 0 && <span className="inline-flex items-center gap-1 text-amber-800"><Sparkles className="w-3.5 h-3.5" /> {suggestedCount} pre-filled from similar past transactions (highlighted). Change anything before importing.</span>}
        </p>
        {rows.some(r => r.locked) && <span className="text-xs text-emerald-800">{rows.filter(r => r.locked).length} in locked months will be skipped</span>}
      </div>
      <div className="overflow-auto border rounded-lg max-h-[55vh]">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-stone-500 sticky top-0 z-10"><tr>
            <th className="px-2 py-2 text-left font-medium">Date</th><th className="px-2 py-2 text-left font-medium">Provider</th>
            <th className="px-2 py-2 text-right font-medium">Amount</th><th className="px-2 py-2 text-left font-medium">Memo</th>
            <th className="px-2 py-2 text-left font-medium">Category</th><th className="px-2 py-2 text-left font-medium">Store</th>
          </tr></thead>
          <tbody>{rows.map((r, i) => <ReviewRow key={i} r={r} i={i} stores={stores} categoriesFor={categoriesFor} isPositiveType={isPositiveType} onEdit={editRow} />)}</tbody>
        </table>
      </div>
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} data-testid="import-review-back"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <Button className="flex-1 bg-stone-900 text-stone-50 hover:bg-stone-800" disabled={busy || importable === 0} onClick={onConfirm} data-testid="import-review-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} {confirmLabel || `Import ${importable} transactions`}
        </Button>
      </div>
    </div>
  );
};
